import $axios from './apiConnection'
import parseCatch from '@/helpers/handleErrors.js'
import type { WidgetConfig, WidgetTime } from '@/types/dashboard'

export interface ParameterItem {
    label: string
    value: string
}

export type WidgetParameterBulkUpdateItem = {
    WidgetID: number
    WidgetConfig?: WidgetConfig[]
    WidgetTime?: WidgetTime
}

export type WidgetParameterBulkUpdatePayload = {
    Widgets: WidgetParameterBulkUpdateItem[]
}

export type WidgetParameterBulkUpdateFailedWidget = {
    Status: string | number
    WidgetID: string | number
    Description?: string
}

export type WidgetParameterBulkUpdateResponse = {
    Status?: string | number
    Description?: string
    FailedWidgets?: WidgetParameterBulkUpdateFailedWidget[]
}

export interface QuestionItem {
    label: string
    description: string
    data_type?: string
}

export interface QuestionCategory {
    categoryName: string
    categoryID: number
    questions: QuestionItem[]
}

export type QuestionCategoriesMap = Record<string, QuestionCategory>

export interface AiTemplate {
    TemplateID: number
    Name: string
    Template: string
    AccountID: number
    LanguageID: number
}

export interface PromptTemplateCategory {
    AccountID: number
    PromptTemplateCategoryID: number
    PromptTemplateCategoryName: string
    PromptTemplateCategoryDescription: string
}

/* A template can sit in several categories at once, hence the ids rather than a single one */
export interface CategorisedPromptTemplate {
    PromptTemplateID: number
    Name: string
    Description: string
    SystemPrompt: string
    UserPrompt: string
    PromptTemplateCategoryIDs: Array<number>
}

export interface CategoriesPromptTemplates {
    templates: Array<CategorisedPromptTemplate>
    categories: Array<PromptTemplateCategory>
}

export const widgetParameterApi = {
    /**
     * Fetches CDR (Call Detail Record) parameters list
     * @returns List of CDR parameter options
     */
    async getCDRList (): Promise<ParameterItem[]> {
        try {
            const { Data } = await $axios.get('/WidgetParameter/CDRList/')
            return Data || []
        } catch (e) {
            parseCatch(e, false, 'Widget Parameter CDR List')
            return []
        }
    },

    /**
     * Fetches Calls Custom Variables list
     * @returns List of custom variable parameter options
     */
    async getCallsCustomVariablesList (): Promise<ParameterItem[]> {
        try {
            const { Data } = await $axios.get('/WidgetParameter/CallsCustomVariablesList/')
            return Data || []
        } catch (e) {
            parseCatch(e, false, 'Widget Parameter Custom Variables List')
            return []
        }
    },

    async getQuestionsList (dataTypes: Array<string> = []): Promise<QuestionCategoriesMap> {
        try {
            const { Data } = await $axios.post('/WidgetParameter/Questions/', {
                DataType: dataTypes
            })
            return Data || {}
        } catch (e) {
            parseCatch(e, false, 'Widget Parameter Questions List')
            return {}
        }
    },

    async bulkUpdate (
        data: WidgetParameterBulkUpdatePayload
    ): Promise<WidgetParameterBulkUpdateResponse | undefined> {
        try {
            return await $axios.post('/WidgetParameter/BulkUpdate/', data)
        } catch (e) {
            parseCatch(e, true, 'Bulk Update Widgets')
        }
    },

    /**
     * Fetches AI Template list
     * @param body - Request body (user will customize this)
     * @returns List of AI templates
     */
    async getAiTemplateList (body: Record<string, unknown> = { fetchGlobals: true }): Promise<AiTemplate[]> {
        try {
            const { Data } = await $axios.post('/Summarizer/CDRTemplateList/', body)
            return Data || []
        } catch (e) {
            parseCatch(e, false, 'AI Template List')
            return []
        }
    },

    /**
     * Fetches the analysis templates together with the categories they are filed under
     * @returns Templates and categories, both empty on failure
     */
    async getCategoriesPromptTemplatesList (): Promise<CategoriesPromptTemplates> {
        try {
            const { Data } = await $axios.get('/Summarizer/CategoriesPromptTemplatesList/')
            return {
                templates: Data?.templates || [],
                categories: Data?.categories || []
            }
        } catch (e) {
            parseCatch(e, false, 'Categories Prompt Templates List')
            return {
                templates: [],
                categories: []
            }
        }
    }
}
